import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowCounterClockwise, ArrowUp, Check, CheckCircle, Clock, X, XCircle } from '@phosphor-icons/react';

import {
  approveSignup,
  cancelSignup,
  listTeamSignups,
  promoteSignup,
  rejectSignup,
  reopenSignup,
  type Signup,
  type SignupStatus,
} from '@/api/signups';
import { listTeamMembers, type TeamMemberRole } from '@/api/teams';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { TablePagination } from '@/components/ui/table-pagination';
import { SearchInput } from '@/components/ui/search-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useConfirm } from '@/components/ConfirmDialog';

// Sentinel for the "no filter" option — Radix Select disallows an empty
// string as an item value, so the dropdowns use 'all' and map it to '' at
// the boundary.
const ALL = 'all';

// SignupQueueTable renders one team's applications queue — filter pills
// across the top, table below, row actions wired to approve / reject /
// promote / cancel. Extracted from the old Signups.tsx so the Anmälningar
// overview can drop it inline under each expanded team row.
//
// Caller is expected to render a header context (which team this is for);
// the table itself focuses on the data + actions.

export interface SignupQueueTableProps {
  slug: string;
  teamID: string;
  canManage: boolean;
  // isOrgAdmin lifts the self-decision block: org admins (owner /
  // administrator) may approve/reject/promote their own application, so a
  // sole admin who signs themselves up isn't deadlocked. Leaders/coaches
  // still see the read-only "your application" hint. The backend enforces
  // the same rule (signups.ErrSelfDecision, admin-exempt).
  isOrgAdmin: boolean;
  statusFilter: SignupStatus | '';
  onStatusFilterChange: (s: SignupStatus | '') => void;
}

export function SignupQueueTable({
  slug,
  teamID,
  canManage,
  isOrgAdmin,
  statusFilter,
  onStatusFilterChange,
}: SignupQueueTableProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  // "Alla" (no status filter) shows the team's ROSTER — every member on the
  // team, including those added by import / by hand who never went through a
  // signup. The status pills show the signup pipeline instead. So the empty
  // statusFilter swaps the data source from signups to members.
  const showRoster = statusFilter === '';

  const signupsQuery = useQuery({
    queryKey: ['signups', slug, teamID, statusFilter],
    queryFn: () => listTeamSignups(token, slug, teamID, statusFilter),
    enabled: !!token && !!slug && !!teamID && !showRoster,
  });

  // One generous page — a single team's roster is bounded; the dedicated
  // Members page is the place to page/manage a very large roster. Uses the
  // team-members endpoint (not the generic members list) because it carries
  // each member's per-team role, which the roster view's Role column needs.
  const rosterLimit = 200;
  const rosterQuery = useQuery({
    queryKey: ['team-roster', slug, teamID],
    queryFn: () => listTeamMembers(token, slug, teamID, { limit: rosterLimit }),
    enabled: !!token && !!slug && !!teamID && showRoster,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['signups', slug, teamID] });
    void queryClient.invalidateQueries({
      queryKey: ['signup-summary', slug],
    });
  };

  const cancelMutation = useMutation({
    mutationFn: (signupID: string) => cancelSignup(token, slug, signupID),
    onSuccess: invalidate,
  });
  const approveMutation = useMutation({
    mutationFn: (signupID: string) => approveSignup(token, slug, signupID),
    onSuccess: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['teams', slug] });
      void queryClient.invalidateQueries({ queryKey: ['members', slug] });
      // Approving creates a member — refresh the "Alla" roster view.
      void queryClient.invalidateQueries({ queryKey: ['team-roster', slug, teamID] });
    },
  });
  const rejectMutation = useMutation({
    mutationFn: (signupID: string) => rejectSignup(token, slug, signupID),
    onSuccess: invalidate,
  });
  const promoteMutation = useMutation({
    mutationFn: (signupID: string) => promoteSignup(token, slug, signupID),
    onSuccess: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['teams', slug] });
      void queryClient.invalidateQueries({ queryKey: ['members', slug] });
      void queryClient.invalidateQueries({ queryKey: ['team-roster', slug, teamID] });
    },
  });
  const reopenMutation = useMutation({
    mutationFn: (signupID: string) => reopenSignup(token, slug, signupID),
    onSuccess: invalidate,
  });

  const onCancel = async (s: Signup) => {
    const ok = await confirm({
      title: t('signups.confirm.cancelTitle'),
      body: t('signups.confirm.cancelBody', {
        name: `${s.first_name} ${s.last_name}`,
      }),
      confirmLabel: t('signups.confirm.cancelCta'),
      tone: 'destructive',
    });
    if (!ok) return;
    cancelMutation.mutate(s.id);
  };
  const onApprove = async (s: Signup) => {
    const ok = await confirm({
      title: t('signups.confirm.approveTitle'),
      body: t('signups.confirm.approveBody', {
        name: `${s.first_name} ${s.last_name}`,
      }),
      confirmLabel: t('signups.confirm.approveCta'),
    });
    if (!ok) return;
    approveMutation.mutate(s.id);
  };
  const onReject = async (s: Signup) => {
    const ok = await confirm({
      title: t('signups.confirm.rejectTitle'),
      body: t('signups.confirm.rejectBody', {
        name: `${s.first_name} ${s.last_name}`,
      }),
      confirmLabel: t('signups.confirm.rejectCta'),
      tone: 'destructive',
    });
    if (!ok) return;
    rejectMutation.mutate(s.id);
  };
  const onPromote = async (s: Signup) => {
    const ok = await confirm({
      title: t('signups.confirm.promoteTitle'),
      body: t('signups.confirm.promoteBody', {
        name: `${s.first_name} ${s.last_name}`,
      }),
      confirmLabel: t('signups.confirm.promoteCta'),
    });
    if (!ok) return;
    promoteMutation.mutate(s.id);
  };
  const onReopen = async (s: Signup) => {
    const ok = await confirm({
      title: t('signups.confirm.reopenTitle'),
      body: t('signups.confirm.reopenBody', {
        name: `${s.first_name} ${s.last_name}`,
      }),
      confirmLabel: t('signups.confirm.reopenCta'),
    });
    if (!ok) return;
    reopenMutation.mutate(s.id);
  };

  const signups = signupsQuery.data?.signups ?? [];
  const roster = rosterQuery.data?.members ?? [];
  const rosterTotal = rosterQuery.data?.total ?? 0;

  // Client-side paging for the expander tables (both the roster and each
  // signup-status tab). Counts per team are bounded, so we page the already-
  // fetched set rather than round-tripping. Page resets to 1 whenever the tab
  // changes so you never land on an out-of-range page after switching.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Free-text search across the active tab — name / email / phone, client-side
  // over the fetched rows (same bounded set the pagination works on).
  const [search, setSearch] = useState('');
  // Role filter for the roster view (Alla) — '' = all roles. Client-side over
  // the fetched roster, same as search. Only meaningful while showRoster.
  const [roleFilter, setRoleFilter] = useState<TeamMemberRole | ''>('');
  // Back to page 1 whenever the tab, the search term, or the role filter changes.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, search, roleFilter]);

  const q = search.trim().toLowerCase();
  const matchPerson = (name: string, email: string, phone: string) =>
    q === '' ||
    name.toLowerCase().includes(q) ||
    email.toLowerCase().includes(q) ||
    phone.toLowerCase().includes(q);
  const filteredRoster = roster.filter(
    (m) =>
      (roleFilter === '' || m.role === roleFilter) &&
      matchPerson(`${m.first_name} ${m.last_name}`, m.email, m.phone),
  );
  const filteredSignups = signups.filter((s) =>
    matchPerson(`${s.first_name} ${s.last_name}`, s.email ?? '', s.phone ?? ''),
  );
  const pageStart = (page - 1) * pageSize;
  const pagedSignups = filteredSignups.slice(pageStart, pageStart + pageSize);
  const pagedRoster = filteredRoster.slice(pageStart, pageStart + pageSize);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={statusFilter === '' ? ALL : statusFilter}
          onValueChange={(v) =>
            onStatusFilterChange(v === ALL ? '' : (v as SignupStatus))
          }
        >
          <SelectTrigger
            className="w-full sm:w-44"
            aria-label={t('signups.filter.statusLabel')}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('signups.filter.all')}</SelectItem>
            <SelectItem value="submitted">{t('signups.status.submitted')}</SelectItem>
            <SelectItem value="confirmed">{t('signups.status.confirmed')}</SelectItem>
            <SelectItem value="waitlisted">{t('signups.status.waitlisted')}</SelectItem>
            <SelectItem value="rejected">{t('signups.status.rejected')}</SelectItem>
            <SelectItem value="cancelled">{t('signups.status.cancelled')}</SelectItem>
          </SelectContent>
        </Select>
        {showRoster && (
          <Select
            value={roleFilter === '' ? ALL : roleFilter}
            onValueChange={(v) =>
              setRoleFilter(v === ALL ? '' : (v as TeamMemberRole))
            }
          >
            <SelectTrigger
              className="w-full sm:w-40"
              aria-label={t('signups.filter.roleLabel')}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('signups.filter.allRoles')}</SelectItem>
              <SelectItem value="athlete">{t('signups.role.athlete')}</SelectItem>
              <SelectItem value="leader">{t('signups.role.leader')}</SelectItem>
            </SelectContent>
          </Select>
        )}
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('signups.search.placeholder')}
          aria-label={t('signups.search.placeholder')}
          wrapperClassName="w-full sm:w-56"
        />
      </div>

      {showRoster ? (
        rosterQuery.isPending ? (
          <TableSkeleton columns={4} />
        ) : filteredRoster.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-border bg-muted/40 p-6 text-center text-sm text-muted-foreground">
            {roster.length === 0
              ? t('signups.roster.empty')
              : t('signups.search.noMatches')}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto rounded-2xl border border-border bg-background">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('signups.col.name')}</TableHead>
                    <TableHead>{t('signups.col.role')}</TableHead>
                    <TableHead>{t('signups.col.email')}</TableHead>
                    <TableHead>{t('signups.col.phone')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedRoster.map((m) => (
                    <TableRow key={m.member_id}>
                      <TableCell className="font-medium">
                        {m.first_name} {m.last_name}
                      </TableCell>
                      <TableCell>
                        <RoleBadge role={m.role} />
                      </TableCell>
                      <TableCell>{m.email || '—'}</TableCell>
                      <TableCell>{m.phone || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <TablePagination
              total={filteredRoster.length}
              page={page}
              size={pageSize}
              pageSizes={[10, 25, 50]}
              onPageChange={setPage}
              onSizeChange={(n) => {
                setPageSize(n);
                setPage(1);
              }}
            />
            {rosterTotal > roster.length && (
              <p className="text-xs text-muted-foreground">
                {t('signups.roster.truncated', {
                  shown: roster.length,
                  total: rosterTotal,
                })}
              </p>
            )}
          </>
        )
      ) : signupsQuery.isPending ? (
        <TableSkeleton columns={7} />
      ) : filteredSignups.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border bg-muted/40 p-6 text-center text-sm text-muted-foreground">
          {signups.length === 0
            ? t('signups.queue.empty')
            : t('signups.search.noMatches')}
        </p>
      ) : (
        <>
        <div className="overflow-x-auto rounded-2xl border border-border bg-background">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('signups.col.name')}</TableHead>
                <TableHead>{t('signups.col.email')}</TableHead>
                <TableHead>{t('signups.col.phone')}</TableHead>
                <TableHead>{t('signups.col.birthDate')}</TableHead>
                <TableHead>{t('signups.col.status')}</TableHead>
                <TableHead>{t('signups.col.submittedAt')}</TableHead>
                {canManage && (
                  <TableHead
                    className="w-12"
                    aria-label={t('signups.col.actions')}
                  />
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedSignups.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">
                    {s.first_name} {s.last_name}
                  </TableCell>
                  <TableCell>{s.email}</TableCell>
                  <TableCell>{s.phone || '—'}</TableCell>
                  <TableCell>{s.birth_date || '—'}</TableCell>
                  <TableCell>
                    <StatusBadge status={s.status} />
                  </TableCell>
                  <TableCell>{formatDate(s.submitted_at)}</TableCell>
                  {canManage && (
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {/* Separation of duties: leaders/coaches can't decide
                            their own application (the backend rejects it too).
                            Show a quiet hint instead of
                            approve/reject/promote/reopen; withdrawing (cancel)
                            your own row stays allowed. Org admins are exempt so
                            a sole admin isn't deadlocked. */}
                        {s.is_own_signup && !isOrgAdmin ? (
                          <span
                            className="text-xs italic text-muted-foreground"
                            title={t('signups.ownSignup.hint')}
                          >
                            {t('signups.ownSignup.label')}
                          </span>
                        ) : (
                          <>
                            {s.status === 'submitted' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => onApprove(s)}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-pill text-muted-foreground hover:bg-primary/15 hover:text-foreground"
                                  aria-label={t('signups.actions.approve')}
                                  title={t('signups.actions.approve')}
                                >
                                  <Check
                                    className="h-4 w-4"
                                    weight="bold"
                                    aria-hidden
                                  />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => onReject(s)}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-pill text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                  aria-label={t('signups.actions.reject')}
                                  title={t('signups.actions.reject')}
                                >
                                  <XCircle className="h-4 w-4" aria-hidden />
                                </button>
                              </>
                            )}
                            {s.status === 'waitlisted' && (
                              <button
                                type="button"
                                onClick={() => onPromote(s)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-pill text-muted-foreground hover:bg-primary/15 hover:text-foreground"
                                aria-label={t('signups.actions.promote')}
                                title={t('signups.actions.promote')}
                              >
                                <ArrowUp
                                  className="h-4 w-4"
                                  weight="bold"
                                  aria-hidden
                                />
                              </button>
                            )}
                            {s.status === 'rejected' && (
                              <button
                                type="button"
                                onClick={() => onReopen(s)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-pill text-muted-foreground hover:bg-primary/15 hover:text-foreground"
                                aria-label={t('signups.actions.reopen')}
                                title={t('signups.actions.reopen')}
                              >
                                <ArrowCounterClockwise
                                  className="h-4 w-4"
                                  weight="bold"
                                  aria-hidden
                                />
                              </button>
                            )}
                          </>
                        )}
                        {s.status !== 'cancelled' &&
                          s.status !== 'rejected' && (
                            <button
                              type="button"
                              onClick={() => onCancel(s)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-pill text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                              aria-label={t('signups.actions.cancel')}
                              title={t('signups.actions.cancel')}
                            >
                              <X className="h-4 w-4" aria-hidden />
                            </button>
                          )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          total={filteredSignups.length}
          page={page}
          size={pageSize}
          pageSizes={[10, 25, 50]}
          onPageChange={setPage}
          onSizeChange={(n) => {
            setPageSize(n);
            setPage(1);
          }}
        />
        </>
      )}
    </div>
  );
}

function RoleBadge({ role }: { role: TeamMemberRole }) {
  const { t } = useTranslation('common');
  // Leaders are the few — give them the solid lime fill (the design system's
  // filled-pill highlight) so they pop in a roster of athletes; athletes get
  // the quiet muted pill. A 20%-lime tint reads as plain text on the white
  // row, so leaders get the full fill, not a tint.
  const cls =
    role === 'leader'
      ? 'bg-primary text-primary-foreground'
      : 'bg-muted text-muted-foreground';
  return (
    <span
      className={`inline-flex items-center rounded-pill px-2 py-0.5 text-xs font-semibold ${cls}`}
    >
      {t(`signups.role.${role}`)}
    </span>
  );
}

function StatusBadge({ status }: { status: SignupStatus }) {
  const { t } = useTranslation('common');
  const cls =
    status === 'confirmed'
      ? 'bg-primary/20 text-primary-foreground'
      : status === 'submitted'
        ? 'bg-amber-100 text-amber-900'
        : status === 'awaiting_payment'
          ? 'bg-blue-100 text-blue-900'
          : status === 'waitlisted'
            ? 'bg-purple-100 text-purple-900'
            : status === 'cancelled'
              ? 'bg-muted text-muted-foreground'
              : 'bg-red-100 text-red-900';
  const Icon =
    status === 'confirmed'
      ? CheckCircle
      : status === 'submitted' || status === 'awaiting_payment'
        ? Clock
        : XCircle;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-xs font-semibold ${cls}`}
    >
      <Icon className="h-3.5 w-3.5" weight="bold" aria-hidden />
      {t(`signups.status.${status}`)}
    </span>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}
